// 🔔 치과 설정 — 「원장 호출」 (2026-10-08, 플래그 doctorCall strict — 페이지 안에서 게이트)
// 명세 docs/명세_원장호출_웹_2026-10-08.md §1 · 서버 계약 docs/계약_원장호출_서버_2026-10-08.md §3·§6
//  - 저장 = doctorCalls/{clinicId}.settings 6키 통째로(대표만 — rules). 직원은 읽기 전용(같은 값 표시)
//  - 상황 문구 4칸 = 사전 목록 10개 중 선택(자유 입력 없음, 4칸 서로 달라야 함)
//  - 주간 집계 = getDoctorCallWeeklyStats(대표만). 체어·시간대·상황 단위만 — 사람별 집계 없음(직원 감시로 비치지 않게)
import React, { useEffect, useState } from 'react';
import { BellRing } from 'lucide-react';
import PageHeader from '../common/PageHeader';
import { getTimingMedian } from '../../utils/dcTiming';
import DoctorCallPcDownload from './DoctorCallPcDownload';   // 🖥️ 원장실 PC 프로그램 내려받기(대표 10-13)
import DoctorCallReceivers from './DoctorCallReceivers';   // 📱 받는 기기 관리(대표 10-13 Q8)   // ⏱️ 최근 응답 시간 중앙값(이 탭에서 누른 것, 10-08)
import { getFeatures, isDoctorCallOn } from '../../utils/features';
import { subscribeClinic, saveSettings, getDoctorCallWeeklyStats } from '../../services/doctorCallApi';
import {
    DEFAULT_SETTINGS, normalizeSettings, PHRASES, SITUATION_KEYS, SITUATION_COLORS, URGENT_ALT_COLOR, phraseText,
    SOUND_UP_CHOICES, DESK_SHOW_CHOICES, CHAIRS_MIN, CHAIRS_MAX, doctorCallError,
} from '../../utils/doctorCall';

const COL_NAME = Object.freeze({ ready: '1열(초록)', anesthesia: '2열(파랑)', check: '3열(주황)', urgent: '4열(급함)' });
const secText = (s) => (s >= 60 ? `${s / 60}분` : `${s}초`);
const avgText = (v) => (v === null || v === undefined || !Number.isFinite(Number(v)) ? '-' : (Number(v) >= 60 ? `${Math.floor(Number(v) / 60)}분 ${Math.round(Number(v) % 60)}초` : `${Math.round(Number(v))}초`));

// KST 기준 이번 주 월요일 'YYYY-MM-DD' (+ weeks 이동)
function kstMonday(weeksAgo) {
    const k = new Date(Date.now() + 9 * 3600000);
    const wd = (k.getUTCDay() + 6) % 7;   // 월=0
    const m = new Date(Date.UTC(k.getUTCFullYear(), k.getUTCMonth(), k.getUTCDate() - wd - weeksAgo * 7));
    return `${m.getUTCFullYear()}-${String(m.getUTCMonth() + 1).padStart(2, '0')}-${String(m.getUTCDate()).padStart(2, '0')}`;
}

function DoctorCallSettings({ user }) {
    // 훅은 전부 최상단 — early return 위 (Dashboard 사고)
    const clinicId = user?.companyId || user?.uid;
    const isOwner = user?.userType !== 'staff' && (!user?.companyId || user.companyId === user?.uid);
    const businessType = user?.businessType || user?.companyBusinessType;
    const isClinic = businessType === 'clinic' || businessType === 'dental' || businessType === '치과';

    const [flagOn, setFlagOn] = useState(null);   // null=로딩
    const [saved, setSaved] = useState(null);     // 서버 값(null=로딩)
    const [draft, setDraft] = useState(null);
    const [loadErr, setLoadErr] = useState('');
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState(null);   // { type:'ok'|'err', text }
    const [weeksAgo, setWeeksAgo] = useState(0);
    const [stats, setStats] = useState({ loading: false, data: null, err: '' });
    // ⏱️ 최근 20회 "누름→서버 응답" 중앙값(window.__dcTiming — 이 탭 메모리). 2초마다 다시 읽는다
    const [timing, setTiming] = useState(() => ({ dc: getTimingMedian('doctorCall'), rc: getTimingMedian('reception') }));
    useEffect(() => {
        const t = setInterval(() => setTiming({ dc: getTimingMedian('doctorCall'), rc: getTimingMedian('reception') }), 2000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        let alive = true;
        getFeatures().then(f => { if (alive) setFlagOn(isDoctorCallOn(f)); })
            .catch((e) => { console.error('[원장 호출 설정] 플래그 조회 실패:', e?.message || e); if (alive) setFlagOn(false); });
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        if (!flagOn || !clinicId || !isClinic) return undefined;
        let alive = true;
        const unsub = subscribeClinic(clinicId, (d, meta) => {
            if (!alive || meta?.hasPendingWrites) return;
            setSaved(d.settings);
            setDraft(prev => prev || d.settings);
        }, (e) => {
            console.error('[원장 호출 설정] 설정 조회 실패:', e?.code || e?.message || e);
            if (!alive) return;
            setSaved(normalizeSettings(null)); setDraft(prev => prev || normalizeSettings(null));
            setLoadErr(doctorCallError(e, '저장된 설정을 불러오지 못해 기본값을 표시합니다. 새로고침해 주세요.'));
        });
        return () => { alive = false; unsub(); };
    }, [flagOn, clinicId, isClinic]);

    useEffect(() => {
        if (!flagOn || !isOwner || !isClinic) return undefined;
        let alive = true;
        setStats({ loading: true, data: null, err: '' });
        getDoctorCallWeeklyStats(kstMonday(weeksAgo))
            .then(d => { if (alive) setStats({ loading: false, data: d, err: '' }); })
            .catch(e => {
                console.error('[원장 호출 설정] 주간 집계 조회 실패:', e?.code || e?.message || e, e?.details?.code || '');
                if (!alive) return;
                // not-found(함수 없음)만 "준비 중". internal 등 나머지는 실제 오류로 보인다(가리지 않음)
                const code = String(e?.code || '');
                const notReady = code.includes('not-found') && !e?.details?.code;
                setStats({ loading: false, data: null, err: notReady ? '집계 서버 준비 중입니다. 잠시 후 다시 열어 주세요.' : doctorCallError(e, `주간 집계를 불러오지 못했습니다(오류: ${code.replace('functions/', '') || '알 수 없음'}). 잠시 후 다시 시도해 주세요.`) });
            });
        return () => { alive = false; };
    }, [flagOn, isOwner, isClinic, weeksAgo]);

    if (!isClinic) {
        return (
            <div style={S.container}>
                <PageHeader icon={BellRing} title="원장 호출" />
                <div style={S.emptyBox}>{'치과 계정만 사용할 수 있는 화면입니다.'}</div>
            </div>
        );
    }
    if (flagOn === false) {
        return (
            <div style={S.container}>
                <PageHeader icon={BellRing} title="원장 호출" />
                <div style={S.emptyBox}>{'원장 호출 기능이 아직 열리지 않았습니다.'}</div>
            </div>
        );
    }
    if (flagOn === null || !draft) {
        return (
            <div style={S.container}>
                <PageHeader icon={BellRing} title="원장 호출" />
                <div style={S.emptyBox}>{'불러오는 중…'}</div>
            </div>
        );
    }

    const cur = draft;
    const ro = !isOwner || busy;
    const set = (patch) => { setDraft(normalizeSettings({ ...cur, ...patch })); setNotice(null); };
    const setLabel = (col, key) => {
        // 다른 칸이 이미 쓰는 문구면 서로 맞바꾼다(4칸 서로 달라야 함)
        const other = SITUATION_KEYS.find(k => k !== col && cur.labels[k] === key);
        const labels = { ...cur.labels, [col]: key };
        if (other) labels[other] = cur.labels[col];
        set({ labels });
    };
    const dirty = JSON.stringify(normalizeSettings(cur)) !== JSON.stringify(normalizeSettings(saved));
    const save = async () => {
        if (!isOwner || busy) return;
        setBusy(true); setNotice(null);
        try {
            await saveSettings(clinicId, cur);
            setNotice({ type: 'ok', text: '저장했습니다. 열려 있는 진료실·원장 화면에 바로 반영됩니다.' });
        } catch (e) {
            console.error('[원장 호출 설정] 저장 실패:', e?.code || e?.message || e);
            setNotice({ type: 'err', text: doctorCallError(e, '저장하지 못했습니다. 잠시 후 다시 시도해 주세요.') });
        } finally { setBusy(false); }
    };
    const chip = (on) => ({ border: '1px solid ' + (on ? '#0f6b5c' : '#d9dfdc'), background: on ? '#0f6b5c' : '#fff', color: on ? '#fff' : '#17201d', borderRadius: 8, padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: ro ? 'default' : 'pointer', minHeight: 36 });
    const st = stats.data;

    return (
        <div style={S.container}>
            <PageHeader icon={BellRing} title="원장 호출" />
            {!isOwner && <div style={S.readonly}>{'설정은 치과 대표만 바꿀 수 있습니다. 지금 값을 보여 드립니다.'}</div>}
            {loadErr && <div style={S.err}>{loadErr}</div>}

            <div style={S.box}>
                <div style={S.sectionTitle}>{'체어 수'}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button type="button" disabled={ro || cur.chairs <= CHAIRS_MIN} onClick={() => set({ chairs: cur.chairs - 1 })} style={S.pm}>{'−'}</button>
                    <b style={{ fontSize: 22, minWidth: 54, textAlign: 'center' }}>{`${cur.chairs}대`}</b>
                    <button type="button" disabled={ro || cur.chairs >= CHAIRS_MAX} onClick={() => set({ chairs: cur.chairs + 1 })} style={S.pm}>{'+'}</button>
                    <span style={S.sub}>{`${CHAIRS_MIN}~${CHAIRS_MAX}대`}</span>
                </div>
            </div>

            <div style={S.box}>
                <div style={S.sectionTitle}>{'상황 문구 (4칸)'}</div>
                {SITUATION_KEYS.map(col => (
                    <div key={col} style={S.item}>
                        <span style={{ ...S.itemLabel, color: col === 'urgent' && cur.urgentColor === 'orange' ? URGENT_ALT_COLOR : SITUATION_COLORS[col] }}>{COL_NAME[col]}</span>
                        <select value={cur.labels[col]} disabled={ro} onChange={(e) => setLabel(col, e.target.value)} style={S.select} aria-label={`${COL_NAME[col]} 문구`}>
                            {PHRASES.map(p => <option key={p.key} value={p.key}>{p.text}</option>)}
                        </select>
                    </div>
                ))}
                <div style={S.help}>{'목록에서만 고릅니다(직접 입력 없음). 다른 칸이 쓰는 문구를 고르면 두 칸이 서로 바뀝니다. 칸 색은 열마다 고정입니다.'}</div>
            </div>

            <div style={S.box}>
                <div style={S.sectionTitle}>{'원장 응답 선택지'}</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button type="button" disabled={ro} onClick={() => set({ ackSet: 'std' })} style={chip(cur.ackSet === 'std')}>{'가는 중 / 2분 / 5분'}</button>
                    <button type="button" disabled={ro} onClick={() => set({ ackSet: 'min135' })} style={chip(cur.ackSet === 'min135')}>{'1분 / 3분 / 5분'}</button>
                </div>
            </div>

            <div style={S.box}>
                <div style={S.sectionTitle}>{'미응답일 때'}</div>
                <div style={S.item}>
                    <span style={S.itemLabel}>{'깜빡임·원장 소리 크게'}</span>
                    {SOUND_UP_CHOICES.map(s => <button key={s} type="button" disabled={ro} onClick={() => set({ soundUpSec: s })} style={chip(cur.soundUpSec === s)}>{`${secText(s)} 뒤`}</button>)}
                </div>
                <div style={S.item}>
                    <span style={S.itemLabel}>{'데스크에 표시'}</span>
                    {DESK_SHOW_CHOICES.map(s => <button key={s} type="button" disabled={ro || s < cur.soundUpSec} onClick={() => set({ deskShowSec: s })} style={{ ...chip(cur.deskShowSec === s), ...(s < cur.soundUpSec ? { opacity: 0.4 } : null) }}>{`${secText(s)} 뒤`}</button>)}
                </div>
                <div style={S.help}>{'데스크 표시는 소리 크게보다 같거나 늦어야 합니다.'}</div>
            </div>

            <div style={S.box}>
                <div style={S.sectionTitle}>{'「급함」 칸 색'}</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button type="button" disabled={ro} onClick={() => set({ urgentColor: 'red' })} style={chip(cur.urgentColor === 'red')}><span style={{ ...S.dot, background: SITUATION_COLORS.urgent }} />{'빨강'}</button>
                    <button type="button" disabled={ro} onClick={() => set({ urgentColor: 'orange' })} style={chip(cur.urgentColor === 'orange')}><span style={{ ...S.dot, background: URGENT_ALT_COLOR }} />{'진한 주황'}</button>
                </div>
            </div>

            {isOwner && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14 }}>
                    <button type="button" disabled={busy || !dirty} onClick={save} style={{ ...S.btnPri, opacity: busy || !dirty ? 0.5 : 1 }}>{busy ? '저장 중…' : '저장'}</button>
                    <button type="button" disabled={busy || !dirty} onClick={() => { setDraft(saved || normalizeSettings(DEFAULT_SETTINGS)); setNotice(null); }} style={S.btn}>{'되돌리기'}</button>
                    {notice && <span style={notice.type === 'ok' ? S.okText : S.errText}>{notice.text}</span>}
                </div>
            )}

            <DoctorCallPcDownload />

            <DoctorCallReceivers isOwner={isOwner} />

            <div style={S.box}>
                <div style={{ ...S.sectionTitle, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span>{'주간 집계'}</span>
                    {isOwner && (
                        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                            <button type="button" disabled={weeksAgo >= 12 || stats.loading} onClick={() => setWeeksAgo(w => w + 1)} style={S.btnSm}>{'‹ 지난 주'}</button>
                            <span style={S.sub}>{st ? `${st.weekStart} ~ ${st.weekEnd}` : kstMonday(weeksAgo)}</span>
                            <button type="button" disabled={weeksAgo <= 0 || stats.loading} onClick={() => setWeeksAgo(w => w - 1)} style={S.btnSm}>{'다음 주 ›'}</button>
                        </span>
                    )}
                </div>
                {!isOwner ? <div style={S.sub}>{'주간 집계는 치과 대표만 볼 수 있습니다.'}</div>
                    : stats.loading ? <div style={S.sub}>{'불러오는 중…'}</div>
                    : stats.err ? <div style={S.errText}>{stats.err}</div>
                    : st ? (
                        <>
                            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13.5, marginBottom: 10 }}>
                                <span>{'호출 '}<b>{st.total || 0}</b>{'건'}</span>
                                <span>{'응답 '}<b>{st.acked || 0}</b>{'건'}</span>
                                <span>{'응답 없이 끝남 '}<b>{st.unanswered || 0}</b>{'건'}</span>
                                <span>{'평균 응답 '}<b>{avgText(st.avgAckSec)}</b></span>
                            </div>
                            <table style={S.table}>
                                <thead><tr><th style={S.th}>{'상황'}</th><th style={S.thR}>{'호출'}</th><th style={S.thR}>{'평균 응답'}</th></tr></thead>
                                <tbody>
                                    {SITUATION_KEYS.map(k => {
                                        const v = (st.bySituation && st.bySituation[k]) || {};
                                        return <tr key={k}><td style={S.td}>{phraseText((saved || cur).labels[k])}</td><td style={S.tdR}>{v.count || 0}</td><td style={S.tdR}>{avgText(v.avgAckSec)}</td></tr>;
                                    })}
                                </tbody>
                            </table>
                            <table style={S.table}>
                                <thead><tr><th style={S.th}>{'체어'}</th><th style={S.thR}>{'호출'}</th><th style={S.thR}>{'응답'}</th><th style={S.thR}>{'평균 응답'}</th></tr></thead>
                                <tbody>
                                    {(st.byChair || []).length === 0 && <tr><td style={S.td} colSpan={4}>{'이 주에는 호출이 없습니다.'}</td></tr>}
                                    {(st.byChair || []).map(r => <tr key={r.chair}><td style={S.td}>{`${r.chair}번`}</td><td style={S.tdR}>{r.count || 0}</td><td style={S.tdR}>{r.acked || 0}</td><td style={S.tdR}>{avgText(r.avgAckSec)}</td></tr>)}
                                </tbody>
                            </table>
                            <table style={S.table}>
                                <thead><tr><th style={S.th}>{'시간대'}</th><th style={S.thR}>{'호출'}</th><th style={S.thR}>{'평균 응답'}</th></tr></thead>
                                <tbody>
                                    {(st.byHour || []).length === 0 && <tr><td style={S.td} colSpan={3}>{'이 주에는 호출이 없습니다.'}</td></tr>}
                                    {(st.byHour || []).map(r => <tr key={r.hour}><td style={S.td}>{`${r.hour}시대`}</td><td style={S.tdR}>{r.count || 0}</td><td style={S.tdR}>{avgText(r.avgAckSec)}</td></tr>)}
                                </tbody>
                            </table>
                        </>
                    ) : null}
                <div style={S.help}>{'체어·시간대·상황 단위로만 셉니다. 누가 눌렀는지·누가 응답했는지는 기록하지 않습니다. 호출 기록은 90일 뒤 자동으로 지워집니다.'}</div>
            </div>

            <div style={S.help}>{'원장 호출은 진료실과 원장실 사이의 알림 도구입니다. 체어 번호·상황·시각만 저장하고 환자 정보는 저장하지 않습니다.'}</div>
            <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 8 }}>
                {`최근 응답 시간 중앙값 — 원장 호출 ${timing.dc.median === null ? '기록 없음' : `${timing.dc.median} ms (${timing.dc.count}회)`} · 접수 ${timing.rc.median === null ? '기록 없음' : `${timing.rc.median} ms (${timing.rc.count}회)`} · 이 창에서 누른 것만, 새로고침하면 초기화`}
            </div>
        </div>
    );
}

const S = {
    container: { padding: 20, maxWidth: 720, margin: '0 auto' },
    emptyBox: { border: '1px solid #e2e8f0', borderRadius: 12, padding: 26, textAlign: 'center', fontSize: 14, color: '#9ca3af', background: '#fff' },
    box: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 18px', marginBottom: 14 },
    sectionTitle: { fontSize: 14.5, fontWeight: 700, color: '#0f172a', marginBottom: 10 },
    help: { fontSize: 12.5, color: '#64748b', lineHeight: 1.6, marginTop: 10 },
    readonly: { fontSize: 12.5, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 11px', marginBottom: 10 },
    item: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 4px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' },
    itemLabel: { fontSize: 13.5, fontWeight: 700, color: '#0f172a', minWidth: 150 },
    select: { padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, minWidth: 160, background: '#fff' },
    pm: { width: 44, height: 44, border: '1px solid #cbd5e1', background: '#fff', borderRadius: 10, fontSize: 22, fontWeight: 700, cursor: 'pointer' },
    dot: { display: 'inline-block', width: 12, height: 12, borderRadius: '50%', marginRight: 6, verticalAlign: 'middle' },
    sub: { fontSize: 12, color: '#94a3b8' },
    btnPri: { border: '1px solid #0f6b5c', background: '#0f6b5c', color: '#fff', borderRadius: 8, padding: '8px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
    btn: { border: '1px solid #cbd5e1', background: '#fff', color: '#0f172a', borderRadius: 8, padding: '8px 14px', fontSize: 13.5, cursor: 'pointer' },
    btnSm: { border: '1px solid #cbd5e1', background: '#fff', color: '#0f172a', borderRadius: 7, padding: '3px 10px', fontSize: 12, cursor: 'pointer' },
    okText: { fontSize: 12.5, color: '#065f46' },
    errText: { fontSize: 12.5, color: '#8a2020' },
    err: { marginBottom: 10, background: '#fdecec', border: '1px solid #f5cfcf', color: '#8a2020', borderRadius: 8, padding: '8px 11px', fontSize: 12.5 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 12 },
    th: { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e2e8f0', color: '#64748b', fontWeight: 600 },
    thR: { textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #e2e8f0', color: '#64748b', fontWeight: 600 },
    td: { padding: '6px 8px', borderBottom: '1px solid #f1f5f9' },
    tdR: { padding: '6px 8px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
};

export default DoctorCallSettings;
